import React, { useContext, useState, useEffect } from "react";
import styles from "./TopMenu.module.css";
import MenuIcon from "@mui/icons-material/Menu";
import SchoolIcon from "@mui/icons-material/School";
import PreviewIcon from "@mui/icons-material/Preview";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import EditNotificationsIcon from "@mui/icons-material/EditNotifications";
import PasswordIcon from "@mui/icons-material/Password";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import GridViewIcon from "@mui/icons-material/GridView";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import FeedbackIcon from "@mui/icons-material/Feedback";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import LogoutIcon from "@mui/icons-material/Logout";
import SummarizeIcon from "@mui/icons-material/Summarize";
import authService from "../../services/authService";
import { useNavigate } from "react-router";
import {
  ListItemText,
  Divider,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  SwipeableDrawer,
  useMediaQuery,
  Link,
  ListItemIcon,
} from "@mui/material";
import { setSearchText } from "../PatientListPage/PatientListPage";
import theme from "../theme";
import FeedbackDialog from "../Feedback/Feedback";
import SettingsIcon from "@mui/icons-material/Settings";
import * as Constants from "../../constants";
import { CLINCAL_TRIAL_USER_TYPE } from "../../constants";
import AppContext from "../../context/AppContext";
import { useApolloClient } from "@apollo/client/react";
import NotificationInfoMenu from "../NotificationInfoMenu/NotificationInfoMenu";
import { isTenantVet } from "../../services/tenantService";
import { signOutHelper } from "../../utils/mutationHelper";
import { eResultsOptions, getSiteOption, showEorderButton } from "../../utils/siteOptions";
import { validateProvider } from "../../utils/provider";
import { useQuery } from "@apollo/client/react";
import { GetBillingCode } from "../EorderDialog/types/GetBillingCode";
import GetBillingCodeQuery from "../EorderDialog/GetBillingCodeQuery";
import { getBiometricCapability } from "../../services/mobilityService";
import DialogBoxBiometric from "../DialogBox/DialogBoxBiometric";
import DialogBoxBiometricSuccess from "../DialogBox/DialogBoxBiometricSuccess";
import DialogBoxBiometricDisable from "../DialogBox/DialogBoxBiometricDisable";
import useBiometric from "../MFAAuthentication/useBiometric";
import { FaceIdIcon } from "../../utils/faceIdIcon";
import ListAltIcon from '@mui/icons-material/ListAlt';

export type TopmenuProps = {
  display?: string;
  ml?: number;
  style?: React.CSSProperties;
};

const TopMenu: React.FC<TopmenuProps> = (props) => {
  const navigate = useNavigate();
  const isDesktop = useContext(AppContext).isDesktop;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  // Biometric enable/enrol prompt is driven by the shared useBiometric hook, so
  // the manual (menu-triggered) flow behaves identically to the auto-offer on
  // the patient list page.
  const biometric = useBiometric();
  // Only show "Biometrics Support" when running inside the mobile app AND the
  // device actually supports biometrics. Default hidden; enabled after the
  // native capability check below confirms support.
  const [useBiometrics, setUseBiometrics] = useState(false);
  // Whether biometrics is currently enabled for this user on this device, so the
  // menu can offer "Turn off biometrics" vs "Biometrics Support" (enrol).
  const [biometricEnabled, setBiometricEnabled] = useState<boolean>(() =>
    authService.getBiometricEnabled(authService.getUsername()),
  );
  // Confirmation dialog for turning biometrics off.
  const [openDisableBiometric, setOpenDisableBiometric] = useState(false);
  const client = useApolloClient();
  const [showGenEorderButton, setShowGenEorderButton] = useState(false);
  const [isValidProvider, setIsValidProvider] = useState(false);
  const eOrderFeature = (getSiteOption(eResultsOptions.EORDER_FEATURE) as string) === "ON";
  const siteOptions = getSiteOption(eResultsOptions.EORDER_BLACKLIST) as string[];
  const [provno] = useState(authService.getProviderSelected() ? authService.getProviderSelected() : "0");
  
  const { data: billingCodeData } = useQuery<GetBillingCode>(GetBillingCodeQuery, {});

  const mobility = authService.isMobility();

  const [state, setState] = React.useState({
    right: false,
  });

  const signOut = async () => {
    try {
      signOutHelper(undefined);
      
      setSearchText("");
      localStorage.removeItem("searchtext");
      if (authService.isCognito()) {
        authService.clearAll();
        await client.clearStore();
        const signoutUrl = import.meta.env.VITE_LOGOUT_URL;
        if (signoutUrl) {
          window.location.replace(`${signoutUrl}`);
        }
      } else {
        authService.clearAll();
        await client.clearStore();
        // Flag the navigation as originating from a sign-out so LoginPage does
        // not auto-trigger the biometric cold-launch prompt (it should show the
        // biometric button screen and let the user choose).
        navigate("/login", { state: { fromLogout: true } });
      }
    } catch {
      if (authService.isCognito()) {
        const signoutUrl = import.meta.env.VITE_LOGOUT_URL;
        if (signoutUrl) {
          window.location.replace(`${signoutUrl}`);
        }
      } else {
        navigate("/login", { state: { fromLogout: true } });
      }
    }
  };

  const toggleDrawer = (side: "right", open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }

    setState({ ...state, [side]: open });
  };

  React.useEffect(() => {
    let cancelled = false;

    // Biometrics is a mobile-only feature. Outside the Flutter shell we never
    // show the menu item. It is also unavailable to SSO/Cognito sign-ins
    // (server-side enrolment is disallowed for Cognito users), so hide it then.
    if (!mobility || authService.isCognito()) {
      setUseBiometrics(false);
      return;
    }

    // Ask the native shell whether this device supports biometrics.
    const checkBiometricSupport = async () => {
      try {
        const capability = await getBiometricCapability();
        if (!cancelled) {
          const supported = Boolean(capability.supported) && !!capability.platform;
          setUseBiometrics(supported);
          if (supported) {
            // Resolve the device's biometric label up front so the menu shows
            // "Face ID"/"Fingerprint" rather than the default "Biometrics".
            biometric.refreshMethodName();
          }
        }
      } catch {
        if (!cancelled) {
          setUseBiometrics(false);
        }
      }
    };

    checkBiometricSupport();

    // Backwards-compatible fallback: some shells push a "biometricsSupported"
    // message instead of responding to the capability request.
    const receiveMessage = (event: any): void => {
      if (event.data && event.data === "biometricsSupported" && !authService.isCognito()) {
        setUseBiometrics(true);
      }
    };
    window.addEventListener("message", receiveMessage, false);

    return () => {
      cancelled = true;
      window.removeEventListener("message", receiveMessage, false);
    };
  }, [mobility]);

  React.useEffect(() => {
    if (billingCodeData?.getBillingCode) {
      const nonMbsProviders = billingCodeData.getBillingCode.map((b:any) => b.providerNo);

      if (provno === "0") {
        authService.getProviders().map((value: string) => {
          if (nonMbsProviders.includes(value) || (showEorderButton(siteOptions, value) && validateProvider(value))) {
            setShowGenEorderButton(true);
            setIsValidProvider(true);

            const selectedBillingCode = billingCodeData?.getBillingCode.find((bc) => bc.providerNo === value);
                        
            if (selectedBillingCode?.eorderEmail != null) {
              authService.setEorderEmail(selectedBillingCode.eorderEmail);
            }
          }
        });
      } else {
        if (nonMbsProviders.includes(provno) || (showEorderButton(siteOptions, provno) && validateProvider(provno))) {
          setShowGenEorderButton(true);
          setIsValidProvider(true);

          const selectedBillingCode = billingCodeData?.getBillingCode.find((bc) => bc.providerNo === provno);
          authService.setEorderEmail(selectedBillingCode?.eorderEmail || "");
        }
      }
    }
  }, [billingCodeData, provno, siteOptions]);

  window.addEventListener(
    "beforeunload",
    function () {
      if (window.opener === null || window.opener === undefined) {
        setSearchText("");
        localStorage.removeItem("searchtext");
      }
    },
    false,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
  };

  const handleChangePasswd = () => {
    navigate("/change-password");
  };

  const handleChangeEmail = () => {
    navigate("/account-settings");
  };

  const handleNotificationChange = () => {
    navigate("/notification-settings");
  };

  const handleClickFeedback = () => {
    setOpenFeedbackDialog(true);
  };

  const handleFeedbackClose = () => {
    setOpenFeedbackDialog(false);
  };

  const handleHospitalAdmin = () => {
    navigate("/groupadmin");
  };

  const handleChangePatientList = () => {
    navigate("/patients");
  };

  // When an enrolment succeeds (success modal shown), reflect the new enabled
  // state so the menu switches to "Turn off biometrics".
  useEffect(() => {
    if (biometric.successOpen) {
      setBiometricEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biometric.successOpen]);

  const handleBiometrics = () => {
    if (biometricEnabled) {
      // Already enabled: confirm before turning it off. Resolve the device's
      // biometric label first so the dialog shows "Face ID"/"Fingerprint"
      // rather than the default "Biometrics".
      biometric.refreshMethodName();
      setOpenDisableBiometric(true);
    } else {
      // Not enabled: open the same enable-biometrics prompt shown on the patient
      // list. enable()/decline() (wired on the dialog) drive the enrolment.
      biometric.openEnrolPrompt();
    }
  };

  const handleConfirmDisableBiometric = async () => {
    const ok = await biometric.disable();
    if (ok) {
      setBiometricEnabled(false);
    }
    setOpenDisableBiometric(false);
  };

  const handleCancelDisableBiometric = () => {
    setOpenDisableBiometric(false);
  };

  const handlePreviousTestRequests = () => {
    navigate("/previous-test-requests");
  };

  const handleChangeReportView = (reportView: string) => {
    authService.setReportView(reportView);
    //@todo: To make a smoother action, we should use the props to send back the view change.
    window.location.reload();
  };

  const trailingText = biometric.methodName.toLowerCase().includes("fingerprint") ? " Unlock" : "";

  const menus = [
    {
      name: "Patient List",
      text: "Patient List",
      icon: <ListAltIcon />,
      type: "menu",
      url: authService.isClinicalTrial() && !authService.isMobility() ? "/patient-episodes" : "/patients",
    },
    {
      name: "Account Settings",
      text: "Account Settings",
      icon: <SettingsIcon />,
      type: "menu",
      url: "/account-settings",
    },
    {
      name: "Notifications Settings",
      text: "Notifications Settings",
      type: "menu",
      icon: <EditNotificationsIcon />,
      url: "/notification-settings",
    },
    {
      name: "Change Password",
      text: "Change Password",
      type: "menu",
      icon: <PasswordIcon />,
      showAt: !authService.isCognito(),
      url: "/change-password",
    },
    { type: "divider" },
    {
      name: "Standard View",
      text: "Standard View",
      type: "menu",
      icon: <GridViewIcon />,
      showAt:
        window.location.href.indexOf("/patient/") > -1 &&
        window.location.href.indexOf("/labnumber") > -1 &&
        authService.getClinicalTrial() !== CLINCAL_TRIAL_USER_TYPE.Standard &&
        authService.getReportView() === "2",
      onClickEvent: () => handleChangeReportView("1"),
    },
    {
      name: "Combined View",
      text: "Combined View",
      type: "menu",
      icon: <CallMergeIcon />,
      showAt:
        window.location.href.indexOf("/patient/") > -1 &&
        window.location.href.indexOf("/labnumber") > -1 &&
        authService.getClinicalTrial() !== CLINCAL_TRIAL_USER_TYPE.Standard &&
        authService.getReportView() === "1",
      onClickEvent: () => handleChangeReportView("2"),
    },
    {
      type: "divider",
      showAt:
        window.location.href.indexOf("/patient/") > -1 &&
        window.location.href.indexOf("/labnumber/") > -1 &&
        authService.getClinicalTrial() !== CLINCAL_TRIAL_USER_TYPE.Standard,
    },
    {
      name: "Previous eOrders",
      text: "Previous eOrders",
      icon: <PreviewIcon />,
      type: "menu",
      showAt: eOrderFeature && showGenEorderButton && isValidProvider,
      url: "/previous-test-requests",
    },
    {
      name: "CPD Portal",
      text: "CPD Portal",
      icon: <SchoolIcon />,
      type: "menu",
      showAt:
        !isTenantVet() &&
        (authService.getCPDEnrolled().length > 0 || authService.validProviders()),
      url: "/cpd-programs",
    },
    {
      name: "Hospital Reports",
      text: "Hospital Reports",
      icon: <SummarizeIcon />,
      type: "menu",
      showAt:
        !isTenantVet() && authService.showHospitalReport() && window.location.href.indexOf("/hospital-reports") <= 0,
      url: "/hospital-reports",
    },
    {
      name: "Back to eResults",
      text: "Back to eResults",
      icon: <SummarizeIcon />,
      type: "menu",
      showAt:
        !isTenantVet() && authService.showHospitalReport() && window.location.href.indexOf("/hospital-reports") > -1,
      url: "/patients",
    },
    { type: "divider" },
    {
      name: "Give Us Feedback",
      text: "Give Us Feedback",
      type: "menu",
      icon: <FeedbackIcon />,
      onClickEvent: () => handleClickFeedback(),
    },
    {
      name: "Group Admin",
      text: "Group Admin",
      icon: <SupervisorAccountIcon />,
      showAt: authService.isHospitalAdmin(),
      classes: styles.MenuItem3,
      type: "menu",
      url: "/groupadmin",
    },

    {
      name: "User Guide",
      text: "User Guide",
      type: "link",
      icon: <LiveHelpIcon />,
      url: Constants.getUserGuide(),
    },
    { type: "divider" },

    {
      name: biometricEnabled ? `Disable ${biometric.methodName} ${trailingText}` : `Enable ${biometric.methodName} ${trailingText}`,
      text: biometricEnabled ? `Disable ${biometric.methodName} ${trailingText}` : `Enable ${biometric.methodName} ${trailingText}`,
      type: "menu",
      icon:
        biometric.methodName === "Face ID" || biometric.methodName === "Face Unlock" ? (
          <FaceIdIcon />
        ) : (
          <FingerprintIcon />
        ),
      showAt: useBiometrics,
      onClickEvent: () => handleBiometrics(),
    },
    {
      name: "Sign out",
      text: "Sign out",
      type: "menu",
      icon: <LogoutIcon />,
      onClickEvent: () => handleLogout(),
    },
  ];

  const mobileMenus = [
    {
      name: "Patient List",
      text: "Patient List",
      onClickEvent: () => handleChangePatientList(),
      icon: <ListAltIcon />,
      type: "menu",
    },
    {
      name: "Account Settings",
      text: "Account Settings",
      onClickEvent: () => handleChangeEmail(),
      icon: <SettingsIcon />,
      type: "menu",
    },
    {
      name: "Notification Settings",
      text: "Notification Settings",
      icon: <EditNotificationsIcon />,
      onClickEvent: () => handleNotificationChange(),
      type: "menu",
    },
    {
      name: "Change Password",
      text: "Change Password",
      icon: <PasswordIcon />,
      showAt: !authService.getSharedAccount() && !authService.isCognito(),
      onClickEvent: () => handleChangePasswd(),
      type: "menu",
    },
    { type: "divider" },
    {
      name: "Previous eOrders",
      text: "Previous eOrders",
      icon: <PreviewIcon />,
      showAt: eOrderFeature && showGenEorderButton && isValidProvider,
      onClickEvent: () => handlePreviousTestRequests(),
      type: "menu",
    },
    {
      name: "CPD Portal",
      text: "CPD Portal",
      icon: <SchoolIcon />,
      type: "menu",
      showAt:
        !isTenantVet() &&
        (authService.getCPDEnrolled().length > 0 || authService.validProviders()),
      url: "/cpd-programs",
    },
    {
      name: "Hospital Reports",
      text: "Hospital Reports",
      icon: <SummarizeIcon />,
      type: "menu",
      showAt:
        !isTenantVet() && authService.showHospitalReport() && window.location.href.indexOf("/hospital-reports") <= 0,
      url: "/hospital-reports",
    },
    {
      name: "Back to eResults",
      text: "Back to eResults",
      icon: <SummarizeIcon />,
      type: "menu",
      showAt:
        !isTenantVet() && authService.showHospitalReport() && window.location.href.indexOf("/hospital-reports") > -1,
      url: "/patients",
    },
    { type: "divider" },
    {
      name: "Give Us Feedback",
      text: "Give Us Feedback",
      icon: <FeedbackIcon />,
      onClickEvent: () => handleClickFeedback(),
      type: "menu",
    },
    {
      name: "Group Admin",
      text: "Group Admin",
      icon: <SupervisorAccountIcon />,
      showAt: authService.isHospitalAdmin(),
      onClickEvent: () => handleHospitalAdmin(),
      type: "menu",
    },
    {
      name: "User Guide",
      text: "User Guide",
      icon: <LiveHelpIcon />,
      type: "link",
      url: Constants.getUserGuide(),
    },
    { type: "divider" },
    {
      name: biometricEnabled ? `Disable ${biometric.methodName} ${trailingText}` : `Enable ${biometric.methodName} ${trailingText}`,
      text: biometricEnabled ? `Disable ${biometric.methodName} ${trailingText}` : `Enable ${biometric.methodName} ${trailingText}`,
      icon:
        biometric.methodName === "Face ID" || biometric.methodName === "Face Unlock" ? (
          <FaceIdIcon />
        ) : (
          <FingerprintIcon />
        ),
      showAt: useBiometrics,
      onClickEvent: () => handleBiometrics(),
      type: "menu",
    },

    {
      name: "Sign out",
      text: "Sign out",
      icon: <LogoutIcon />,
      type: "menu",
      onClickEvent: () => handleLogout(),
    },
  ];
  const MenuElement = (inputMenu: any) => {
    return (
      <div>
        {inputMenu.inputMenu.map((menuItem: any, index: any) => {
          if (menuItem.type === "menu" && menuItem.showAt != false) {
            return (
              <MenuItem key={index}>
                {menuItem.icon && <ListItemIcon> {menuItem.icon}</ListItemIcon>}
                <Button
                  onClick={() => {
                    if (menuItem.onClickEvent) {
                      menuItem.onClickEvent();
                    } else if (menuItem.url) {
                      navigate(menuItem.url);
                    }
                  }}
                  className={menuItem.classes ? menuItem.classes : styles.MenuItem}
                  color="secondary"
                >
                  {menuItem.name}
                </Button>
              </MenuItem>
            );
          } else if (menuItem.type === "divider" && menuItem.showAt != false) {
            return <Divider key={index} className={styles.Divider} />;
          } else if (menuItem.type === "header") {
            return (
              <MenuItem key={index}>
                {menuItem.icon && <ListItemIcon> {menuItem.icon}</ListItemIcon>}
                <ListItemText>{menuItem.text}</ListItemText>
              </MenuItem>
            );
          } else if (menuItem.type === "link" && menuItem.showAt != false) {
            return (
              <MenuItem key={index}>
                {menuItem.icon && <ListItemIcon> {menuItem.icon}</ListItemIcon>}
                <Link href={Constants.getUserGuide()} target="blank" className={styles.MenuItemLinkDesktop}>
                  User Guide
                </Link>
              </MenuItem>
            );
          } else {
            return null;
          }
        })}
      </div>
    );
  };

  return (
    <Box display={props.display} ml={props.ml}>
      <FeedbackDialog open={openFeedbackDialog} fullscreen={fullscreen} onClose={handleFeedbackClose} />
      <DialogBoxBiometric
        open={biometric.promptOpen}
        methodName={biometric.methodName}
        loading={biometric.loading}
        onEnable={biometric.enable}
        onDecline={biometric.decline}
      />
      <DialogBoxBiometricSuccess
        open={biometric.successOpen}
        methodName={biometric.methodName}
        onClose={biometric.dismissSuccess}
      />
      <DialogBoxBiometricDisable
        open={openDisableBiometric}
        methodName={biometric.methodName}
        loading={biometric.disabling}
        onConfirm={handleConfirmDisableBiometric}
        onCancel={handleCancelDisableBiometric}
      />
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Button
          aria-controls="simple-menu"
          aria-haspopup="true"
          className={styles.sideMenu}
          onClick={handleClick}
          endIcon={<MenuIcon />}
          style={props.style}
        >
          {authService.getUsername().toUpperCase()}
        </Button>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MenuElement inputMenu={menus} />
        </Menu>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        {!isDesktop ? <NotificationInfoMenu /> : ""}
        <IconButton aria-label="delete" onClick={toggleDrawer("right", true)} className={styles.sideMenu}>
          <MenuIcon />
        </IconButton>
        <SwipeableDrawer
          anchor="right"
          open={state.right}
          onClose={toggleDrawer("right", false)}
          onOpen={toggleDrawer("right", true)}
        >
          <MenuElement inputMenu={mobileMenus} />
        </SwipeableDrawer>
      </Box>
    </Box>
  );
};
export default TopMenu;
